import React, { memo } from 'react';
import { Box, Typography } from '@mui/material';
import type { ReportBlock } from '../../../types/backend';
import ParagraphBlockView from './ParagraphBlockView';
import HeadingBlockView from './HeadingBlockView';
import MetricsBlockView from './MetricsBlockView';
import TableBlockView from './TableBlockView';
import CalloutBlockView from './CalloutBlockView';
import ChartBlockView from './ChartBlockView';
import TimelineBlockView from './TimelineBlockView';
import CostBlockView from './CostBlockView';
import ListBlockView from './ListBlockView';
import GraphBlockView from './GraphBlockView';
import TreeBlockView from './TreeBlockView';
import DividerBlockView from './DividerBlockView';

interface Props {
  block: ReportBlock;
}

/**
 * Visible, fail-loud fallback for a block whose `kind` we do not recognise (or a
 * malformed payload). We never silently drop a block — a missing renderer is a
 * data/contract bug worth surfacing in the UI and the console.
 */
const UnsupportedBlock: React.FC<{ kind: unknown }> = ({ kind }) => (
  <Box
    role="alert"
    sx={{
      my: 1.5,
      px: 2,
      py: 1.5,
      borderRadius: 2,
      border: '1px dashed',
      borderColor: 'error.main',
      bgcolor: (theme) => theme.palette.error.main,
      color: 'error.contrastText',
    }}
  >
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
      Unsupported block
    </Typography>
    <Typography variant="caption">
      No renderer for block kind: {JSON.stringify(kind)}
    </Typography>
  </Box>
);

const BlockRenderer: React.FC<Props> = memo(({ block }) => {
  switch (block.kind) {
    case 'paragraph':
      return <ParagraphBlockView block={block} />;
    case 'heading':
      return <HeadingBlockView block={block} />;
    case 'metrics':
      return <MetricsBlockView block={block} />;
    case 'table':
      return <TableBlockView block={block} />;
    case 'callout':
      return <CalloutBlockView block={block} />;
    case 'chart':
      return <ChartBlockView block={block} />;
    case 'timeline':
      return <TimelineBlockView block={block} />;
    case 'cost':
      return <CostBlockView block={block} />;
    case 'list':
      return <ListBlockView block={block} />;
    case 'graph':
      return <GraphBlockView block={block} />;
    case 'tree':
      return <TreeBlockView block={block} />;
    case 'divider':
      return <DividerBlockView block={block} />;
    default: {
      const unknownKind = (block as { kind?: unknown }).kind;
      console.warn('[BlockRenderer] Unsupported block kind, refusing to silently skip:', unknownKind, block);
      return <UnsupportedBlock kind={unknownKind} />;
    }
  }
});

BlockRenderer.displayName = 'BlockRenderer';
export default BlockRenderer;
